import {
  getPriorAuthEndpoint,
  MULTI_TIN
} from '@src/apps/PriorAuth/constant';
import {
  checkGeoFlow,
  checkHCO,
  checkIsInternal,
  checkIsSimulationMode,
  checkMultiTin,
  checkSingleTin,
  checkContractIdFlow,
} from '@src/utility/createStorage';
import HttpService from '@src/utility/httpService';
import { config } from '@uhg-abyss/web/tools/config';
import {
  PA_CPT_CODES_BULK_URL,
  PA_CPT_CODES_URL,
  PA_NOT_APPROVED_REASONS_BULK_URL,
  PA_NOT_APPROVED_REASONS_URL,
  PA_TOPFILTER_URL,
  QUICK_FILTER_API_URL
} from './apiUrlConstants';

import {
  CASE_SUBMISSION_BY_CHANNEL_ENDPOINT,
  CASE_SUBMISSION_DETAILS_ENDPOINT,
  MARKET_NAME,
  MARKET_TEXT,
  NPI_FLOW,
  NPI_FLOW_CHANGE,
  NPS_DRIVER_FILTER,
  REGION_STATE_INITIAL,
  REQUESTED_JOURNEY,
  SUBMISSION_DETAILS_ENDPOINT,
  TIN_FLOW,
  TURNAROUND_TIME_ENDPOINT
} from '@src/apps/PriorAuth/constant';
import {
  getJobId,
  getPreviousBaseURL,
  queueJobPolling,
  startJobPolling,
} from '@src/utility/CommonBulkPoll';
import { providerKeyData } from '@src/utility/common.service2';

const _httpService = new HttpService();
const cache = new Map();

if (checkIsInternal()) {
  _httpService.setBaseURL(config('API_DOMAIN_INTERNAL'));
} else {
  _httpService.setBaseURL(config('API_DOMAIN_EXTERNAL'));
}

const tinGroupId = sessionStorage.getItem('tinGroupId')
  ? sessionStorage.getItem('tinGroupId')
  : '';

type PriorAuthRequestPayload = object & { TinGroupId?: unknown };

const buildPriorAuthRequestPayload = (payload: PriorAuthRequestPayload) => {
  const { TinGroupId: _tinGroupId, ...payloadWithoutTinGroupId } = payload;

  return checkContractIdFlow()
    ? payloadWithoutTinGroupId
    : { TinGroupId: tinGroupId, ...payloadWithoutTinGroupId };
};

export const getPriorAuthQuickFiltersData = async (payload: any) => {
  const cacheKey = JSON.stringify(payload);
  if (cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }
  try {
    const response: any = await _httpService.push(
      QUICK_FILTER_API_URL,
      payload
    );
    cache.set(cacheKey, response);
    return response;
  } catch (error) {
    console.error('API call failed:', error);
    return null;
  }
};

export const getQuickFiltersData = (payload) => {
  const fetchData = async () => {
    try {
      const response: any = await _httpService.push(
        'extended-tins/top-filter?items=TIN',
        {
          RequestedJourney: 'provTinListFilter',
          ProvTinListFilter: payload,
        }
      );
      return response;
    } catch {
      return null;
    }
  };
  return fetchData();
};

export const getMetricsData = () => {
  const fetchData = async () => {
    try {
      const response: any = await _httpService.get('/metric-definitions/');
      return response.Data;
    } catch (e) {
      return [];
    }
  };
  return fetchData();
};

export const getInsightCardData = (payload) => {
  _httpService.setBaseURL(getPreviousBaseURL() || '');
const fetchData = async () => {
    try {
      const response: any = await _httpService.push(
        getPriorAuthEndpoint(CASE_SUBMISSION_BY_CHANNEL_ENDPOINT, false),
        payload
      );
      return response.Data;
    } catch (error) {
      console.error('Error fetching insight card data:', error);
      return null;
    }
  };
  return fetchData();
};

export const getCaseSubmissionChannelsData = (payload) => {
  _httpService.setBaseURL(getPreviousBaseURL() || '');
const fetchData = async () => {
    try {
      const response: any = await _httpService.push(
        'prior-authorizations/case-submission-trend/?requestType=CHANNEL',
        payload
      );
      return response.Data;
    } catch (error) {
      console.error('Error fetching insight card data:', error);
      return null;
    }
  };
  return fetchData();
};

export interface CaseSubmissionDetailsPayload {
  TimeFilter: string;
  Tins?: string;
  Npi?: string;
  ProviderSysKey?: string;
  TimeFilterText?: string;
  ProviderType?: string;
  Region?: string;
  State?: string;
  Market?: any[];
}

export interface InsightCardDataPayload {
  TimeFilter: string;
  TimeFilterText?: string;
  ProviderType?: string;
  ProviderSysKey?: string;
  Tins?: string;
  Npi?: string;
  Region?: string;
  State?: string;
  Market?: string;
}

export interface CaseSubmissionDetailsResponse {
  Status: string;
  StatusCode: number;
  Message: string;
  TimeStamp: string;
  Data: CaseSubmissionDetails;
}

export interface SubmissionDetailsResponse {
  Status: string;
  StatusCode: number;
  Message: string;
  TimeStamp: string;
  Data: SubmissionDetails;
}

export interface CaseSubmissionDetails {
  TinCount: number;
  PeriodStart: string;
  PeriodEnd: string;
  ProviderSysKey: string | null;
  SubmittedCount: number;
  SubmittedPercent: number;
  ApprovedCount: number;
  ApprovedPercent: number;
  PriorAuthAutoApprovedCount?: number;
  PriorAuthAutoApprovedPercent?: number;
  PendingCount: number;
  PendingPercent: number;
  NotApprovedCount: number;
  NotApprovedPercent: number;
  CancelledCount: number;
  CancelledPercent: number;
  PartiallyApprovedCount: number;
  PartiallyApprovedPercent: number;
  PriorAuthNotRequiredCount?: number;
  PriorAuthNotRequiredPercent?: number;
}

const getCaseSubmissionDetailsData = async (
  payload: CaseSubmissionDetailsPayload
) => {
  try {
    const endpoint = getPriorAuthEndpoint(
      CASE_SUBMISSION_DETAILS_ENDPOINT,
      false
    );
    const responseData: CaseSubmissionDetailsResponse = await _httpService.push(
      endpoint,
      payload
    );
    return responseData;
  } catch (error) {
    console.error(`Error fetching ${CASE_SUBMISSION_DETAILS_ENDPOINT}:`, error);
    throw error;
  }
};

const getSubmissionDetailsData = async (
  payload: CaseSubmissionDetailsPayload
) => {
  try {
    const endpoint = getPriorAuthEndpoint(SUBMISSION_DETAILS_ENDPOINT, false);
    const responseData: SubmissionDetailsResponse = await _httpService.push(
      endpoint,
      payload
    );
    return responseData;
  } catch (error) {
    console.error(`Error fetching ${SUBMISSION_DETAILS_ENDPOINT}:`, error);
    throw error;
  }
};

// Helper to merge case submission and submission details data
const mergeCaseSubmissionData = (
  caseSubmissionDetails: CaseSubmissionDetails,
  submissionDetails: SubmissionDetails | undefined
) => {
  return {
    ...caseSubmissionDetails,
    PriorAuthAutoApprovedCount:
      submissionDetails?.PriorAuthAutoApprovedCount || 0,
    PriorAuthAutoApprovedPercent:
      submissionDetails?.PriorAuthAutoApprovedPercent || null,
    PriorAuthNotRequiredCount:
      submissionDetails?.PriorAuthNotRequiredCount || 0,
    PriorAuthNotRequiredPercent:
      submissionDetails?.PriorAuthNotRequiredPercent || null,
  };
};

/**
 * Fetch both APIs and return raw merged data (NO transformation)
 * Component will handle all data transformation
 *
 * @param payload - Request payload with filters
 * @returns Promise with merged case submission data
 */
export const getCaseSubmissionRawData = async (
  payload: CaseSubmissionDetailsPayload
): Promise<any> => {
  try {
    // Call both APIs in parallel
    const [caseSubmissionResponse, submissionDetailsResponse] =
      await Promise.all([
        getCaseSubmissionDetailsData(payload),
        getSubmissionDetailsData(payload),
      ]);

    // Check if both responses are successful
    if (caseSubmissionResponse?.StatusCode !== 200) {
      throw new Error(
        caseSubmissionResponse?.Message ||
        'Failed to fetch case submission details'
      );
    }

    const caseSubmissionDetails = caseSubmissionResponse.Data;
    const submissionDetails =
      submissionDetailsResponse?.StatusCode === 200
        ? submissionDetailsResponse.Data
        : undefined;

    // Return raw merged data without any transformation
    return mergeCaseSubmissionData(caseSubmissionDetails, submissionDetails);
  } catch (error) {
    console.error('Error fetching case submission raw data:', error);
    throw error;
  }
};

export interface SubmissionDetails {
  PeriodStart: string;
  PeriodEnd: string;
  ProviderSysKey: string | null;
  PriorAuthApprovedCount: number | null;
  PriorAuthAutoApprovedCount: number | null;
  PriorAuthCancelledCount: number | null;
  PriorAuthNotApprovedCount: number | null;
  PriorAuthPendingCount: number | null;
  PriorAuthNotRequiredCount: number | null;
  PriorAuthSubmittedCount: number | null;
  SubmittedPACount: number | null;
  SubmittedNotificationCount: number | null;
  SubmittedStatusCount: number | null;
  SubmittedUpdateCount: number | null;
  PriorAuthApprovedPercent: number | null;
  PriorAuthAutoApprovedPercent: number | null;
  PriorAuthCancelledPercent: number | null;
  PriorAuthNotApprovedPercent: number | null;
  PriorAuthPendingPercent: number | null;
  PriorAuthNotRequiredPercent: number | null;
  SubmittedPAPercent: number | null;
  SubmittedNotificationPercent: number | null;
}

export interface SubmissionDetailsResult {
  JobId: string;
  Status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  Message: string;
  Result: SubmissionDetails;
}

export interface SubmissionDetailsBulkResponse {
  Status: string;
  StatusCode: number;
  Message: string;
  Data: SubmissionDetailsResult;
  TimeStamp: number;
}

// Helper for polling a bulk job
type PollBulkJobOptions = { onStop?: (stopFn: () => void) => void };
async function pollBulkJob<T = unknown>(
  endpoint: string,
  payload: Record<string, unknown>,
  options?: PollBulkJobOptions
): Promise<T> {
  const jobIdResponse = await getJobId(endpoint, payload);
  const jobId = jobIdResponse.Data.JobId;
  return new Promise<T>((resolve, reject) => {
    queueJobPolling(
      jobId,
      {
        onInProgress: () => {
},
        onCompleted: (result) => {
resolve(result as T);
        },
        onFailed: (errorMessage) => {
          console.error('Bulk Polling - Job failed:', errorMessage);
          reject(new Error(errorMessage));
        },
        onStatusUpdate: (statusResponse) => {
},
      },
      '/job-status/result',
      1000,
      undefined,
      false
    ).then((cleanup) => {
      if (options && typeof options.onStop === 'function') {
        options.onStop(cleanup);
      }
    });
  });
}

// Handling bulk flow for case submission details
export const getCaseSubmissionRawDataBulk = async (
  payload: CaseSubmissionDetailsPayload & { jobId: string },
  options?: { onStop?: (stopFn: () => void) => void }
): Promise<any> => {
  const updatedPayload = buildPriorAuthRequestPayload(payload);
  try {
    // Run both polling jobs in parallel
    const [caseSubmissionDetailsPollingResult, submissionDetailsPollingResult] =
      await Promise.all([
        pollBulkJob<CaseSubmissionDetails>(
          getPriorAuthEndpoint(CASE_SUBMISSION_DETAILS_ENDPOINT, true),
          updatedPayload,
          options
        ),
        pollBulkJob<SubmissionDetails>(
          getPriorAuthEndpoint(SUBMISSION_DETAILS_ENDPOINT, true),
          updatedPayload,
          options
        ),
      ]);

    return mergeCaseSubmissionData(
      caseSubmissionDetailsPollingResult,
      submissionDetailsPollingResult
    );
  } catch (error) {
    console.error(
      `Error fetching case submission details data in bulk:`,
      error
    );
    throw error;
  }
};

//Handling bulk flow for Insight Card Data
export const getInsightCardDataBulk = async (
  payload: InsightCardDataPayload,
  options?: { onStop?: (stopFn: () => void) => void }
): Promise<any> => {
  const updatedPayload = buildPriorAuthRequestPayload(payload);
  try {
    const endpoint = getPriorAuthEndpoint(
      CASE_SUBMISSION_BY_CHANNEL_ENDPOINT,
      true
    );
    const pollingResult = await pollBulkJob<any>(
      endpoint,
      updatedPayload,
      options
    );
    return pollingResult;
  } catch (error) {
    console.error(
      `Error fetching ${CASE_SUBMISSION_BY_CHANNEL_ENDPOINT} in bulk:`,
      error
    );
    throw error;
  }
};

//Handling bulk flow for Insight Card Data
export const getCaseSubmissionChannelsDataBulk = async (
  payload: InsightCardDataPayload,
  options?: { onStop?: (stopFn: () => void) => void }
): Promise<any> => {
  const updatedPayload = buildPriorAuthRequestPayload(payload);
  try {
    const endpoint =
      'prior-auth-bulk-process-jobs/case-submission-trend?requestType=CHANNEL';
    const pollingResult = await pollBulkJob<any>(
      endpoint,
      updatedPayload,
      options
    );
    return pollingResult;
  } catch (error) {
    console.error(
      'Error fetching case submission channels data in bulk:',
      error
    );
    throw error;
  }
};

export interface TurnaroundTimePayload {
  TimeFilter: string;
  TimeFilterText?: string;
  ProviderType?: string;
  ProviderSysKey?: string;
  Tins?: string;
  Npi?: string;
}
export interface TurnaroundTimeResponse {
  Status: string;
  StatusCode: number;
  Message: string;
  TimeStamp: string;
  Data: {
    TatAvgExpeditedHours: number;
    TatAvgNonExpeditedHours: number;
    PeriodStart: string;
    PeriodEnd: string;
  };
}
/**
 * Fetch Turnaround Time data
 *
 * @param payload - Request payload with filters
 * @returns Promise with turnaround time data
 */

export const getPriorAuthTurnaroundTimeData = async (
  payload
): Promise<TurnaroundTimeResponse['Data']> => {
  _httpService.setBaseURL(getPreviousBaseURL() || '');
  try {
    const responseData: TurnaroundTimeResponse = await _httpService.push(
      getPriorAuthEndpoint(TURNAROUND_TIME_ENDPOINT, false),
      buildPriorAuthRequestPayload(payload)
    );
    if (responseData?.StatusCode !== 200) {
      throw new Error(
        responseData?.Message || 'Failed to fetch turnaround time data'
      );
    }
    return responseData.Data;
  } catch (error) {
    console.error(`Error fetching ${TURNAROUND_TIME_ENDPOINT}:`, error);
    throw error;
  }
};

export const getPriorAuthTurnaroundTimeDataBulk = async (
  payload: TurnaroundTimePayload & { jobId: string },
  options?: { onStop?: (stopFn: () => void) => void }
): Promise<TurnaroundTimeResponse['Data']> => {
  const updatedPayload = buildPriorAuthRequestPayload(payload);
  try {
    const endpoint = getPriorAuthEndpoint(TURNAROUND_TIME_ENDPOINT, true);
    const pollingResult = await pollBulkJob<TurnaroundTimeResponse['Data']>(
      endpoint,
      updatedPayload,
      options
    );
    return pollingResult;
  } catch (error) {
    console.error(`Error fetching ${TURNAROUND_TIME_ENDPOINT} in bulk:`, error);
    throw error;
  }
};

/**
 * Fetch case submissions over time data - Handles both standard and bulk flows
 * @param payload - Request payload with filters
 * @param key - Optional key flag to trigger bulk processing (highVolumeTinFFlag)
 * @param options - Optional stop polling callback for bulk flow
 * @returns Promise with case submissions over time data
 */
export const getCaseSubmissionsOverTimeData = async (
  payload: any,
  key?: string,
  options?: { onStop?: (stopFn: () => void) => void }
): Promise<any> => {
  // Use the RequestType from payload if provided, otherwise map from GroupBy for backward compatibility
  const requestTypeMap = {
    fundingType: 'LOB',
    channel: 'CHANNEL',
    serviceType: 'SERVICETYPE',
  };

  const requestType =
    payload.RequestType || requestTypeMap[payload.GroupBy] || 'LOB';

  // If key is not provided or false/undefined, use standard flow
  if (!key) {
    _httpService.setBaseURL(getPreviousBaseURL() || '');

    try {
      const response: any = await _httpService.push(
        `prior-authorizations/case-submission-trend/?requestType=${requestType}`,
        buildPriorAuthRequestPayload(payload)
      );
      return response;
    } catch (error) {
      console.error('Error fetching case submissions over time data:', error);
      return null;
    }
  }

  // If key is true, use bulk processing flow
  const updatedPayload = buildPriorAuthRequestPayload(payload);

  try {
    // Submit bulk job - POST /prior-auth-bulk-process-jobs/case-submission-trend?requestType={requestType}
    const bulkEndpoint = `prior-auth-bulk-process-jobs/case-submission-trend?requestType=${requestType}`;

    const pollingResult = await pollBulkJob<any>(
      bulkEndpoint,
      updatedPayload,
      options
    );

    // Transform bulk result to match standard response format
    // Bulk returns: { PeriodStart, PeriodEnd, Result: { "2025-10": [...], ... } }
    // Component expects: { Data: { PeriodStart, PeriodEnd, Result: { "2025-10": [...], ... } } }
    const transformedResult = {
      Data: {
        PeriodStart: (pollingResult as any).PeriodStart,
        PeriodEnd: (pollingResult as any).PeriodEnd,
        Result: (pollingResult as any).Result,
      },
    };

return transformedResult;
  } catch (error) {
    console.error(
      'Error fetching case submissions over time data in bulk:',
      error
    );
    throw error;
  }
};

/**
 * Fetch TIN dropdown options using the same pattern as Reimbursement.
 * For TIN flows (single/multi): calls extended-tins/top-filter?items=MULTITIN with provTinListFilter payload.
 * For HCO flow: calls standard-search/topfilter with ProviderSysKey.
 * Returns array of { id, Name, value, displayName }.
 */
export const getPATinOptionsFromApi = async () => {
  const multiSelectedTins = sessionStorage.getItem('multiSelectedTins');
  const flowType = !multiSelectedTins ? 0 : multiSelectedTins.split(',').length > 1 ? 2 : 1;

  let endpoint: string;
  let payload: any;

  if (flowType === 0) {
    // HCO flow
    endpoint = 'standard-search/topfilter';
    const providerKey = providerKeyData();
    payload = {
      RequestedJourney: 'provTinListFilter',
      ProvTinListFilter: { ProviderSysKey: parseInt(providerKey, 10) }
    };
  } else {
    // Single/Multi TIN — same endpoint as Reimbursement
    endpoint = 'extended-tins/top-filter?items=MULTITIN';
    payload = {
      RequestedJourney: 'provTinListFilter',
      ProvTinListFilter: { Tin: multiSelectedTins }
    };
  }


  try {
    const response: any = await _httpService.push(endpoint, payload);
    if (response?.Data) {
      const tins = response.Data.map((tin: any) => ({
        id: tin.Tin || tin.Value,
        Name: tin.TinName || tin.Tinname || tin.Name,
        value: tin.Tin || tin.Value,
        displayName: tin.Tin || tin.Value,
      }));

      // Write to session — same pattern as Reimbursement
      try {
        const stateStr = sessionStorage.getItem('state');
        const state = stateStr ? JSON.parse(stateStr) : {};
        if (!state.journeyExtendedTinSelected) state.journeyExtendedTinSelected = {};
        if (!state.journeyExtendedTinFilterSelected) state.journeyExtendedTinFilterSelected = {};

        // Store eligible TINs for PA journey (key must match createStorage.js / FilterChipComponent)
        state.journeyExtendedTinSelected.priorAuthTaxId = response.Data;

        if (flowType > 0) {
          state.priorAuthFilters = state.priorAuthFilters || {};
          // Only write if priorAuthFilters.taxId is empty (first visit — don't overwrite cross-journey removal)
          if (!state.priorAuthFilters.taxId || state.priorAuthFilters.taxId.length === 0) {
            state.priorAuthFilters.taxId = response.Data;
          }
          state.journeyExtendedTinFilterSelected['priorAuthTaxId'] = state.priorAuthFilters.taxId;

          const tinCount = response.Data.length;
          if (tinCount === 1) {
            state.extendedCorporateTinSelected = {
              TINNAME: response.Data[0].Tinname || response.Data[0].TinName,
              Tin: response.Data[0].Tin,
              Tinname: response.Data[0].Tinname || response.Data[0].TinName,
              isEnabled: false, isSelected: true
            };
          } else {
            state.extendedCorporateTinSelected = {
              TINNAME: 'All', Tin: 'All Selected TINs', Tinname: 'All',
              isEnabled: true, isSelected: false
            };
          }
        }
        sessionStorage.setItem('state', JSON.stringify(state));
      } catch (e) {
        console.error('[getPATinOptionsFromApi] Error writing session:', e);
      }

      return tins;
    }
    return [];
  } catch (error) {
    console.error('[getPATinOptionsFromApi] API error:', error);
    return [];
  }
};

//top filter changes
export const getPATopFilter = async (
  flow,
  url,
  currentVal?: any,
  selectedVal?: any
) => {
  const highVolumeTinFlag = sessionStorage.getItem('HIVOLUMETINFLOW') === 'true';

  if (highVolumeTinFlag) return;

  const payload = await getPATopFilterPayload(flow, currentVal, selectedVal);

  try {
    const response: any = await _httpService.push(url, payload);
    if ((response as any)?.Data) {
      const tins = (response as any).Data.map((tin: any) => ({
        id: tin.Value,
        Name: tin?.Name,
        value: tin?.Value,
        displayName: tin?.Value,
      }));
      return tins;
    }
  } catch {
    return null;
  }
};
export const getGeoData = async (flow, url, CurrentVals, geoSelectedVals) => {
  const payload = await getPATopFilterPayload(
    flow,
    CurrentVals,
    geoSelectedVals
  );
  try {
    const response: any = await _httpService.push(url, payload);
    if ((response as any)?.Data) {
      if (flow === REGION_STATE_INITIAL) {
        const RegionList = (response as any).Data?.RegionList.map(
          (region: string) => {
            const camelCaseRegion = region
              .toLowerCase()
              .replace(/(?:^|\s|_)(\w)/g, (_, c) => c.toUpperCase());
            return {
              id: region,
              Name: camelCaseRegion,
              value: camelCaseRegion,
              displayName: camelCaseRegion,
            };
          }
        );

        const StateList = (response as any).Data?.StateList.map(
          (state: any) => ({
            id: state.StateAbbr,
            Name: state.StateName,
            value: state.StateName,
            displayName: state.StateName,
          })
        );


        // Sort both RegionList and StateList accordingly
        RegionList.sort((a: any, b: any) => {
          const regionA = a.displayName || '';
          const regionB = b.displayName || '';
          return regionA.localeCompare(regionB, undefined, { numeric: true });
        });

        StateList.sort((a: any, b: any) => {
          const stateA = a.displayName || '';
          const stateB = b.displayName || '';
          return stateA.localeCompare(stateB, undefined, { numeric: true });
        });

        return { RegionList, StateList };
      } else if (flow === MARKET_TEXT) {
        const MarketList = (response as any).Data.map((market: any) => ({
          id: market?.Name,
          Name: market?.Name,
          value: market?.Name,
          displayName: market?.Name,
        }));

        // Sort MarketList accordingly
        MarketList.sort((a: any, b: any) => {
          const marketA = a.displayName || '';
          const marketB = b.displayName || '';
          return marketA.localeCompare(marketB, undefined, { numeric: true });
        });
        return { MarketList };
      }
    }
  } catch {
    return null;
  }
};

// Render the SetllementComponent with the prepared props
export const getPACommonPayload = (
  currentFlow: string | undefined,
  currentVal?: any
) => {
  let priorAuthPayload: any = {};
  const taxID = currentVal?.tin?.value.toString();
  const providerKey = providerKeyData();
  // Only apply Lob filter if not excluded (for "All" trend data, we want all LOBs)
  const standardPeriods = [
    'Last30Days',
    'Last3Months',
    'Last6Months',
    'Last12Months',
    'YTD',
  ];
  const currentState: any = sessionStorage.getItem('state') ? sessionStorage.getItem('state') : '';
  const stored = JSON.parse(currentState);
  const timePeriodValue = stored?.priorAuthFilters?.timePeriod ? stored?.priorAuthFilters?.timePeriod : 'Last12Months';

  // Get timeperiod value - prioritize .value property
  // const timePeriodValue = currentVal?.timeperiod?.value || 'Last12Months';

  const isStandardPeriod = standardPeriods.includes(timePeriodValue);
  const timeFilter = isStandardPeriod ? timePeriodValue : 'CalendarYear';
  const timeFilterText = !isStandardPeriod ? timePeriodValue : undefined;
  const ProviderType = currentVal?.providerType?.value || 'RF';

  switch (currentFlow) {
    case 'SINGLE_TIN': {
      priorAuthPayload = {
        Tin: taxID,
        TimeFilter: timeFilter,
        TimeFilterText: timeFilterText,
        case: 'test',
        // StartDate: startDate,
        // EndDate: endDate,
      };
      break;
    }
    case 'MULTI_TIN': {
      priorAuthPayload = {
        Tin: taxID,
        TimeFilter: timeFilter,
        TimeFilterText: timeFilterText,
        // StartDate: startDate,
        // EndDate: endDate,
      };
      break;
    }
    case 'GEO_FLOW': {
      priorAuthPayload = {
        TimeFilter: timeFilter,
        TimeFilterText: timeFilterText,
        ProviderType: ProviderType,
        // StartDate: startDate,
        // EndDate: endDate,
        Region: currentVal?.geoRegion || '',
        State: currentVal?.geoState || '',
        Market: currentVal?.geoMarket || [],
        case: 'test',
      };
      break;
    }
    case 'HCO': {
      priorAuthPayload = {
        RequestedJourney: 'priorAuthFilter',
        TimeFilter: timeFilter,
        TimeFilterText: timeFilterText,
        ProviderSysKey: providerKey,
        ItemsBy: 'TIN',
        case: 'test',
      };
      break;
    }
    case 'NPI': {
      priorAuthPayload = {
        RequestedJourney: 'priorAuthFilter',
        TimeFilter: timeFilter,
        TimeFilterText: timeFilterText,
        ProviderSysKey: providerKey,
        ItemsBy: 'NPI',
      };
      break;
    }
    default:
  }
  return priorAuthPayload;
};

export const getTinFlowType = (): number => {
  const multiSelectedTins = sessionStorage.getItem('multiSelectedTins');
  if (!multiSelectedTins) {
    return 0; // HCO flow
  }
  const tinCount = multiSelectedTins.split(',').length;
  return tinCount > 1 ? 2 : 1; // 2: Multi-TIN, 1: Single-TIN
};

export const getPACurrentFlow = () => {
  if (checkSingleTin()) return 'SINGLE_TIN';
  if (checkMultiTin()) return 'MULTI_TIN';
  if (checkGeoFlow()) return 'GEO_FLOW';
  if (checkHCO()) return 'HCO';
  return undefined; // Default return if none of the conditions are met
};

export const getTimePeriodOptions = () => {
  const currentYear = new Date().getFullYear();
  return [
    { id: 1, Name: 'Last 30 Days', value: 'Last30Days' },
    { id: 2, Name: 'Last 3 Months', value: 'Last3Months' },
    { id: 3, Name: 'Last 6 Months', value: 'Last6Months' },
    { id: 4, Name: 'Last 12 Months', value: 'Last12Months' },
    { id: 5, Name: 'Year to Date', value: 'YTD' },
    {
      id: 6,
      Name: (currentYear - 1).toString(),
      value: (currentYear - 1).toString(),
    },
    {
      id: 7,
      Name: (currentYear - 2).toString(),
      value: (currentYear - 2).toString(),
    },
  ];
};

// const tins = sessionStorage.getItem('multiSelectedTins');

export const getPATopFilterPayload = async (
  currentFlow,
  currentVal?: any,
  selectedVal?: any
) => {
  let priorAuthPayload: any = {};
  const taxID = currentVal?.tin?.value?.toString();
  const providerKey = providerKeyData();
  // let timeFilter = currentVal?.timeperiod?.value || 'Last12Months';
  // let timeFilterText = undefined;
  const RequestedJourney = REQUESTED_JOURNEY;
  const npsDriverFilter = NPS_DRIVER_FILTER;
  const standardPeriods = [
    'Last30Days',
    'Last3Months',
    'Last6Months',
    'Last12Months',
    'YTD',
  ];

  // Get timeperiod value - prioritize .value property
  const timePeriodValue = currentVal?.timeperiod?.value || 'Last12Months';

  const isStandardPeriod = standardPeriods.includes(timePeriodValue);

  const timeFilter = isStandardPeriod ? timePeriodValue : 'CalendarYear';
  const timeFilterText = timePeriodValue;
  const multiTIn = sessionStorage.getItem('multiTinNavigation') === "true" ? true : false;
  // console.log("geo flow values", geoRegion, ' ', geoState, ' ', geoMarket);
  switch (currentFlow) {
    case MULTI_TIN: {
      priorAuthPayload = {
        RequestedJourney: RequestedJourney,
        PriorAuthFilter: {
          TimeFilter: timeFilter,
          TimeFilterText: timeFilterText,
          ItemsBy: TIN_FLOW,
          ProviderSysKey: "",
          Tins: sessionStorage.getItem('multiSelectedTins') || '',
        },
      };
      break;
    }
    case TIN_FLOW: {
      const multiNpi = getMultiNpiValue();
      priorAuthPayload = {
        RequestedJourney: RequestedJourney,
        PriorAuthFilter: {
          ProviderSysKey: (checkIsInternal() && !checkIsSimulationMode()) ? providerKey : undefined,
          ItemsBy: TIN_FLOW,
          TimeFilter: timeFilter,
          TimeFilterText: timeFilterText,
          Tins: sessionStorage.getItem('multiTinNavigation') !== "true" ? selectedVal : sessionStorage.getItem('multiSelectedTins'),
          ...(multiNpi && { Npi: multiNpi }),
        },
      };
      break;
    }
    case NPI_FLOW: {

      priorAuthPayload = {
        RequestedJourney: REQUESTED_JOURNEY,
        PriorAuthFilter: {
          ProviderSysKey: (sessionStorage.getItem('multiTinNavigation') !== "true" && checkIsInternal() && !checkIsSimulationMode()) ? providerKey : undefined,
          TimeFilter: timeFilter,
          TimeFilterText: timeFilterText,
          ItemsBy: NPI_FLOW,
          Tins: multiTIn ? sessionStorage.getItem('multiSelectedTins') : checkHCO() ? undefined : selectedVal ? getTinValue(selectedVal) : undefined,
          //  Npi: getNpiValue(selectedVal),
        },
      };
      break;
    }
    case 'EXTERNAL_SIMULATION': {

      priorAuthPayload = {
        RequestedJourney: REQUESTED_JOURNEY,
        PriorAuthFilter: {
          TimeFilter: timeFilter,
          TimeFilterText: timeFilterText,
          ItemsBy: NPI_FLOW,
          Tins: getExernalTin(),
        },
      };
      break;
    }
    case NPI_FLOW_CHANGE: {
      const multiNpi = getMultiNpiValue();
      const hasNpi = multiNpi && multiNpi !== 'All' && multiNpi !== 'All Providers';
      priorAuthPayload = {
        ProviderSysKey: (sessionStorage.getItem('multiTinNavigation') !== "true" && checkIsInternal() && !checkIsSimulationMode()) ? providerKey : undefined,
        TimeFilter: timeFilter,
        TimeFilterText: timeFilterText,
        ItemsBy: TIN_FLOW,
        ...(hasNpi && { Npi: multiNpi })
      };
      break;
    }
    case REGION_STATE_INITIAL: {
      priorAuthPayload = {
        RequestedJourney: npsDriverFilter,
        PriorAuthAffordabilityFilter: {
          TimeFilter: timeFilter,
          TimeFilterText: timeFilterText,
          // ItemsBy: STATE_TEXT,
          State: selectedVal && selectedVal?.length > 1 ? selectedVal[1] : '',
          Region: selectedVal && selectedVal?.length > 0 ? selectedVal[0] : '',
        },
      };
      break;
    }
    case MARKET_TEXT: {
      priorAuthPayload = {
        RequestedJourney: REQUESTED_JOURNEY,
        PriorAuthFilter: {
          TimeFilter: timeFilter,
          TimeFilterText: timeFilterText,
          ItemsBy: MARKET_NAME,
          State: selectedVal && selectedVal.length > 1 ? selectedVal[1] : '',
          Region: selectedVal && selectedVal.length > 0 ? selectedVal[0] : '',
        },
      };
      break;
    }
    default: {
      console.warn(`⚠️ DEFAULT case hit - unmatched currentFlow: "${currentFlow}"`, {
        type: typeof currentFlow,
        stringified: JSON.stringify(currentFlow)
      });
    }
  }
  return priorAuthPayload;
};

export const getSingleTinInfo = async (tinId) => {
  // Extract Tin value if tinId is an object with Tin property, otherwise use as is
  const tinValue = typeof tinId === 'object' && tinId.Tin ? tinId.Tin : tinId;

  const payload = {
    RequestedJourney: "provTinListFilter",
    ProvTinListFilter: {
      Tin: tinValue
    }
  }
  try {
    const response: any = await _httpService.push(
      `extended-tins/top-filter?items=TIN`,
      payload
    );

    if (response) {
      return response?.Data[0];
    }
  } catch (error) {
    console.error("here in error in single tin details", error)
  }
}
// ==================== PA Not Approved Services ====================

export interface PANotApprovedPayload {
  TimeFilter: string;
  TimeFilterText?: string;
  Limit: number;
  CaseStatus: string;
  ProviderType?: string;
  Region?: string;
  State?: string;
  Market?: string[];
  Lob?: string;
  Channel?: string;
  Tins?: string;
  Npi?: string;
  Specialty?: string;
  ServiceCategory?: string;
  ServiceType?: string;
}

export interface PANotApprovedResponse {
  Status: string;
  StatusCode: number;
  Message: string;
  TimeStamp: string;
  Data: {
    ProviderSysKey: string | null;
    PeriodStart: string;
    PeriodEnd: string;
    Details: Array<{
      Reason: string;
      Count: number;
    }>;
  };
}

export interface PACPTCodesPayload {
  TimeFilter: string;
  TimeFilterText?: string;
  CaseStatus: string;
  ReasonDesc: string;
  Count: number;
}

export interface PACPTCodesResponse {
  Status: string;
  StatusCode: number;
  Message: string;
  TimeStamp: string;
  Data: {
    PeriodStart: string;
    PeriodEnd: string;
    ProviderSysKey: string | null;
    CptDetails: Array<{
      CptCode: string;
      Description: string;
      Count: number;
      SubmittedCount: number | null;
      LinesSubmittedCount: number | null;
      ClinicalSubmittedCount: number | null;
      AdmnSubmittedCount: number | null;
      AdmnApprovedCount: number | null;
      AdmnNotApprovedCount: number | null;
      ClinicalApprovedCount: number | null;
      ClinicalNotApprovedCount: number | null;
    }>;
  };
}

/**
 * Fetch PA Not Approved Reasons from API
 */
export const fetchPANotApprovedReasons = async (payload: any) => {
  const cacheKey = JSON.stringify(payload);
  if (cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }
  try {
    const response: any = await _httpService.push(PA_NOT_APPROVED_REASONS_URL, payload);
    cache.set(cacheKey, response);
    return response;
  } catch (error) {
    console.error('API call failed:', error);
    return null;
  }
};

export const fetchPANotApprovedReasonsBulk = async (payload: any): Promise<any> => {
  const bulkPayload = buildPriorAuthRequestPayload(payload);

  const previousBaseURL = checkIsInternal()
    ? config('API_DOMAIN_INTERNAL')
    : config('API_DOMAIN_EXTERNAL');

  const bulkApiUrl = config('BULK_PROCESSING_API') || '';
  _httpService.setBaseURL(bulkApiUrl);

  try {
    const jobIdResponse: any = await getJobId(PA_NOT_APPROVED_REASONS_BULK_URL, bulkPayload, bulkApiUrl);
    if (!jobIdResponse || !jobIdResponse.Data || !jobIdResponse.Data.JobId) {
      throw new Error('Failed to get JobId from PA Not Approved bulk API response');
    }

    const jobId = jobIdResponse.Data.JobId;

    const pollingResult = await new Promise<any>((resolve, reject) => {
      startJobPolling(
        jobId,
        {
          onInProgress: () => {
          },
          onCompleted: (result) => {
            resolve(result);
          },
          onFailed: (errorMessage) => {
            console.error('PA Not Approved Bulk Polling - Job failed:', errorMessage);
            reject(new Error(errorMessage));
          },
          onStatusUpdate: (statusResponse) => {
          }
        }
      );
    });


    if (!pollingResult) {
      throw new Error('No result returned from bulk polling API');
    }

    const transformedResponse = {
      Data: {
        Details: pollingResult,
        PeriodStart: '',
        PeriodEnd: '',
        ProviderSysKey: null
      }
    };

    return transformedResponse;
  } catch (error) {
    console.error(`Error fetching PA Not Approved Reasons in bulk:`, error);
    throw error;
  } finally {
    _httpService.setBaseURL(previousBaseURL || '');
  }
};

/**
 * Fetch PA CPT Codes for a specific reason
 */
export const fetchPACPTCodes = async (payload: PACPTCodesPayload) => {
  const cacheKey = JSON.stringify(payload);
  if (cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }
  try {
    const response: PACPTCodesResponse = await _httpService.push(PA_CPT_CODES_URL, payload);
    cache.set(cacheKey, response);
    return response;
  } catch (error) {
    console.error('CPT Codes API call failed:', error);
    return null;
  }
};

/**
 * Fetch PA CPT Codes for a specific reason using bulk processing
 */
export const fetchPACPTCodesBulk = async (payload: any) => {
  const previousBaseURL = checkIsInternal()
    ? config('API_DOMAIN_INTERNAL')
    : config('API_DOMAIN_EXTERNAL');

  const bulkApiUrl = config('BULK_PROCESSING_API') || '';
  _httpService.setBaseURL(bulkApiUrl);

  try {
    const jobIdResponse: any = await getJobId(PA_CPT_CODES_BULK_URL, payload, bulkApiUrl);
    if (!jobIdResponse || !jobIdResponse.Data || !jobIdResponse.Data.JobId) {
      throw new Error('Failed to get JobId from PA CPT Codes bulk API response');
    }
    const jobId = jobIdResponse.Data.JobId;

    const pollingResult: any = await new Promise((resolve, reject) => {
      startJobPolling(
        jobId,
        {
          onInProgress: () => {
          },
          onCompleted: (result) => {
            resolve(result);
          },
          onFailed: (errorMessage) => {
            console.error('PA CPT Codes Bulk Polling - Job failed:', errorMessage);
            reject(new Error(errorMessage));
          },
          onStatusUpdate: (statusResponse) => {
          }
        }
      );
    });


    if (!pollingResult) {
      throw new Error('No result returned from bulk polling API');
    }

    const transformedResponse = {
      Data: {
        CptDetails: pollingResult.CptDetails || pollingResult || [],
        PeriodStart: pollingResult.PeriodStart || '',
        PeriodEnd: pollingResult.PeriodEnd || '',
        ProviderSysKey: null
      }
    };

    return transformedResponse;
  } catch (error) {
    console.error(`Error fetching PA CPT Codes in bulk:`, error);
    throw error;
  } finally {
    _httpService.setBaseURL(previousBaseURL || '');
  }
};

// ==================== CPT Approvals Services ====================

export interface CPTApprovalsResponse {
  Data: {
    CptDetails: Array<{
      CptCode: string;
      Description: string;
      Count: number;
    }>;
    PeriodStart: string;
    PeriodEnd: string;
  };
}

export const fetchCPTApprovalsData = async (payload: any): Promise<CPTApprovalsResponse> => {
  const cacheKey = JSON.stringify(payload);
  if (cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }
  try {
    const response = await _httpService.push(PA_CPT_CODES_URL, payload);
    cache.set(cacheKey, response);
    return response as CPTApprovalsResponse;
  } catch (error) {
    console.error('Error fetching CPT Approvals data:', error);
    throw error;
  }
};

export const fetchCPTApprovalsDataBulk = async (payload: any): Promise<any> => {
  const bulkPayload = buildPriorAuthRequestPayload(payload);

  const previousBaseURL = checkIsInternal()
    ? config('API_DOMAIN_INTERNAL')
    : config('API_DOMAIN_EXTERNAL');

  const bulkApiUrl = config('BULK_PROCESSING_API') || '';
  _httpService.setBaseURL(bulkApiUrl);

  try {
    const jobIdResponse: any = await getJobId(PA_CPT_CODES_BULK_URL, bulkPayload, bulkApiUrl);
    const jobId = jobIdResponse.Data.JobId;
    const pollingResult = await new Promise<any>((resolve, reject) => {
      startJobPolling(
        jobId,
        {
          onInProgress: () => {
          },
          onCompleted: (result) => {
            resolve(result);
          },
          onFailed: (errorMessage) => {
            console.error('CPT Approvals Bulk Polling - Job failed:', errorMessage);
            reject(new Error(errorMessage));
          },
          onStatusUpdate: (statusResponse) => {
          }
        }
      );
    });
    if (!pollingResult) {
      throw new Error('No result returned from bulk polling API');
    }
    const transformedResponse = {
      Data: {
        CptDetails: pollingResult.CptDetails || [],
        PeriodStart: pollingResult.PeriodStart || '',
        PeriodEnd: pollingResult.PeriodEnd || ''
      }
    };
    return transformedResponse;
  } catch (error) {
    console.error(`Error fetching CPT Approvals in bulk:`, error);
    throw error;
  } finally {
    _httpService.setBaseURL(previousBaseURL || '');
  }
};

const getSelectedCorporateTin = () => {
  try {
    const currentUser = sessionStorage.getItem('currentUser');
    if (currentUser) {
      const userData = JSON.parse(currentUser);
      return userData[0]?.selectedTin || null;
    }
  } catch (error) {
    console.warn('Failed to get selectedCorporateTin:', error);
  }
  return null;
};
const getTinValue = (currentVal) => {
  const selectedCorporateTin = getSelectedCorporateTin();
  const isSimulationMode = checkIsSimulationMode();
  if (!isSimulationMode && checkIsInternal()) {
    //return sessionStorage.getItem('multiTinNavigation') !== "true" ? currentVal : sessionStorage.getItem('multiSelectedTins');
    // const parsedState = JSON.parse(sessionStorage.getItem('state'))?.priorAuthFilters;
    let Tins;
    if (sessionStorage.getItem('multiTinNavigation') === "true") {
      Tins = sessionStorage.getItem('multiSelectedTins') || '';
    }
    // else if (parsedState && parsedState.taxId) {
    //   const selectedTins = parsedState.taxId.map((tin: any) => tin.value).join(',');
    //   if (selectedTins !== 'All') {
    //     Tins = selectedTins;
    //   }
    // }
    else {
      Tins = currentVal?.map((val) => val?.id).toString();
    }
    return Tins;
  } else if (isSimulationMode) {
    return selectedCorporateTin?.value || '';
  }
  return '';
}

const getExernalTin = () => {
  const selectedCorporateTin = getSelectedCorporateTin();
  return selectedCorporateTin?.value || '';
}

const getNpiValue = (currentVal) => {
  return JSON.parse(sessionStorage.getItem('state'))?.priorAuthFilters?.npi?.value || currentVal;
}

// Get multiple NPIs from topFilterNpiMultiSelect (comma-separated)
const getMultiNpiValue = (): string => {
  try {
    const state = JSON.parse(sessionStorage.getItem('state') || '{}');
    const topNpis = state?.topFilterNpiMultiSelect;
    if (Array.isArray(topNpis)) {
      const npiValues = topNpis
        .map((n: any) => n.Npi)
        .filter((npi: string) => {
          if (!npi) return false;
          const normalized = String(npi).trim().toLowerCase();
          return normalized !== 'all' && normalized !== 'all providers';
        })
        .join(',');
      return npiValues || '';
    }
  } catch (e) {
    console.warn('Failed to get multi-NPI value:', e);
  }
  return '';
}

// ── Global Filter API functions ──────────────────────────────────────────────

const getGlobalFilterTimeFilter = (): string => {
  try {
    const stored = sessionStorage.getItem('state');
    if (stored) {
      const state = JSON.parse(stored);
      return state?.priorAuthFilters?.timeperiod?.value
        || state?.priorAuthFilters?.timePeriod
        || 'Last12Months';
    }
  } catch { /* ignore */ }
  return 'Last12Months';
};

const getGlobalFilterProviderSysKey = (): string => {
  if (checkIsInternal() && !checkIsSimulationMode()) {
    return providerKeyData() || '';
  }
  return '';
};

const getGlobalFilterSelectedTins = (): string => {
  if (sessionStorage.getItem('HIVOLUMETINFLOW') === 'true') return '';

  // Simulation and external flows already resolve the active single TIN
  // through the same helpers used by the other Prior Auth payloads.
  if (checkIsSimulationMode()) return getTinValue([]) || '';
  if (!checkIsInternal()) return getExernalTin() || '';

  try {
    const stored = sessionStorage.getItem('state');
    if (stored) {
      const state = JSON.parse(stored);
      const tins = state?.priorAuthFilters?.taxId;
      if (Array.isArray(tins)) {
        const stateTins = tins
          .filter((t: any) => t?.Tin && t.Tin !== 'All' && t.Tin !== 'ALL')
          .map((t: any) => t.Tin)
          .join(',');
        if (stateTins) return stateTins;
      }
    }
  } catch { /* ignore */ }
  return sessionStorage.getItem('multiSelectedTins') || '';
};

const getGlobalFilterProviderScope = () => {
  const providerSysKey = getGlobalFilterProviderSysKey();
  return providerSysKey ? { ProviderSysKey: providerSysKey } : {};
};

export interface GlobalFilterOption {
  Value: string;
  Name: string;
}

const mapResponseToFilterOptions = (response: any): GlobalFilterOption[] => {
  if (!response?.Data || !Array.isArray(response.Data)) return [];
  return response.Data
    .filter((item: any) => (item.Value || item.Name) && item.Value !== 'All' && item.Name !== 'All')
    .map((item: any) => ({
      Value: String(item.Value || item.Name),
      Name: item.Name || String(item.Value),
    }));
};

export const fetchGlobalFilterServiceCategoryOptions = async (): Promise<GlobalFilterOption[]> => {
  try {
    const selectedTins = getGlobalFilterSelectedTins();
    const payload = {
      ItemsBy: 'SERVICECATEGORY',
      TimeFilter: getGlobalFilterTimeFilter(),
      ...getGlobalFilterProviderScope(),
      ...(selectedTins && { Tins: selectedTins }),
    };
    const response = await _httpService.push(PA_TOPFILTER_URL, payload);
    return mapResponseToFilterOptions(response);
  } catch (error) {
    console.error('Error fetching service category options:', error);
    return [];
  }
};

export const fetchGlobalFilterNpiOptions = async (tinsOverride?: string): Promise<GlobalFilterOption[]> => {
  try {
    const selectedTins = tinsOverride !== undefined ? tinsOverride : getGlobalFilterSelectedTins();
    const payload = {
      RequestedJourney: 'priorAuthFilter',
      PriorAuthFilter: {
        ItemsBy: 'NPI',
        TimeFilter: getGlobalFilterTimeFilter(),
        ...getGlobalFilterProviderScope(),
         ...(selectedTins && { Tins: selectedTins }),
      },
    };
    const response = await _httpService.push(QUICK_FILTER_API_URL, payload);
    return mapResponseToFilterOptions(response);
  } catch (error) {
    console.error('Error fetching NPI options:', error);
    return [];
  }
};

export const fetchGlobalFilterTaxIdOptions = async (npiOverride?: string): Promise<GlobalFilterOption[]> => {
  try {
    const hasNpi = npiOverride && npiOverride !== 'All' && npiOverride !== 'All Providers';
    const selectedTins = getGlobalFilterSelectedTins();
    const payload = {
      RequestedJourney: 'priorAuthFilter',
      PriorAuthFilter: {
        ItemsBy: 'TIN',
        TimeFilter: getGlobalFilterTimeFilter(),
        ...getGlobalFilterProviderScope(),
        ...(selectedTins && { Tins: selectedTins }),
        // Scope TIN options to the selected NPI so only associated Tax IDs show up
        ...(hasNpi && { Npi: npiOverride }),
      },
    };
    const response = await _httpService.push(QUICK_FILTER_API_URL, payload);
    return mapResponseToFilterOptions(response);
  } catch (error) {
    console.error('Error fetching Tax ID options:', error);
    return [];
  }
};

export const fetchGlobalFilterSpecialtyOptions = async (): Promise<GlobalFilterOption[]> => {
  try {
    const selectedTins = getGlobalFilterSelectedTins();
    const payload = {
      RequestedJourney: 'priorAuthFilter',
      PriorAuthFilter: {
        ItemsBy: 'SPECIALTY',
        TimeFilter: getGlobalFilterTimeFilter(),
        ...getGlobalFilterProviderScope(),
        ...(selectedTins && { Tins: selectedTins }),
      },
    };
    const response = await _httpService.push(QUICK_FILTER_API_URL, payload);
    return mapResponseToFilterOptions(response);
  } catch (error) {
    console.error('Error fetching specialty options:', error);
    return [];
  }
};